/**
 * Consultas e gravações da frota pelo TanStack Query.
 *
 * Papel: aqui ficam `useVeiculos`, `useManutencoes` e `useOficinas`, que mantêm
 * em cache o que a tela lê, e as seis mutações que gravam e mandam a listagem
 * correspondente se atualizar.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_VEICULOS`, e cada
 * mutação invalida apenas o ramo que alterou — com uma exceção. Salvar ou apagar
 * um **veículo** invalida também o histórico: a manutenção mostra a placa e o
 * modelo do carro, e uma correção no cadastro precisa aparecer nas duas telas de
 * uma vez.
 */
import {
  type NovaManutencaoVeiculo,
  type NovaOficina,
  type NovoVeiculo,
} from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarManutencao,
  buscarManutencoes,
  criarManutencao,
  excluirManutencao,
} from '../api/manutencoes';
import {
  atualizarOficina,
  buscarOficinas,
  criarOficina,
  excluirOficina,
} from '../api/oficinas';
import {
  atualizarVeiculo,
  buscarVeiculos,
  criarVeiculo,
  excluirVeiculo,
} from '../api/veiculos';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_VEICULOS = ['veiculos'] as const;

/** Chave da lista da frota. */
export const CHAVE_FROTA = [...CHAVE_VEICULOS, 'frota'] as const;

/** Chave do histórico de manutenção. */
export const CHAVE_MANUTENCOES = [...CHAVE_VEICULOS, 'manutencoes'] as const;

/** Chave da agenda de oficinas. */
export const CHAVE_OFICINAS = [...CHAVE_VEICULOS, 'oficinas'] as const;

/**
 * Lê a frota cadastrada.
 *
 * @returns Resultado da consulta, em ordem de código.
 */
export function useVeiculos() {
  return useQuery({
    queryKey: CHAVE_FROTA,
    queryFn: ({ signal }) => buscarVeiculos(signal),
  });
}

/**
 * Lê o histórico de manutenção.
 *
 * @returns Resultado da consulta, da manutenção mais recente para a mais antiga.
 */
export function useManutencoes() {
  return useQuery({
    queryKey: CHAVE_MANUTENCOES,
    queryFn: ({ signal }) => buscarManutencoes(signal),
  });
}

/**
 * Lê a agenda de oficinas.
 *
 * @returns Resultado da consulta, em ordem alfabética de nome.
 */
export function useOficinas() {
  return useQuery({
    queryKey: CHAVE_OFICINAS,
    queryFn: ({ signal }) => buscarOficinas(signal),
  });
}

/** Variáveis de `useSalvarVeiculo`. */
interface SalvarVeiculoVariaveis {
  /** Identificador do veículo a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoVeiculo;
}

/**
 * Cria ou altera um veículo, conforme venha identificador.
 *
 * Invalida o histórico junto com a frota: a manutenção mostra a placa e o
 * modelo vindos do cadastro, e corrigir o modelo sem invalidar as duas deixaria
 * a tela de manutenções com o texto antigo até alguém recarregar.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarVeiculo() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarVeiculoVariaveis) =>
      id === null ? criarVeiculo(dados) : atualizarVeiculo(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_VEICULOS }),
  });
}

/**
 * Apaga um veículo.
 *
 * Rejeita com 409 quando o veículo ainda tem manutenção, e nesse caso nada foi
 * apagado — a tela precisa dizer isso a quem tentou.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirVeiculo() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirVeiculo(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_VEICULOS }),
  });
}

/** Variáveis de `useSalvarManutencao`. */
interface SalvarManutencaoVariaveis {
  /** Identificador da manutenção a alterar, ou `null` para criar uma nova. */
  id: string | null;
  dados: NovaManutencaoVeiculo;
}

/**
 * Cria ou altera uma manutenção, conforme venha identificador.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarManutencao() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarManutencaoVariaveis) =>
      id === null ? criarManutencao(dados) : atualizarManutencao(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_MANUTENCOES }),
  });
}

/**
 * Apaga uma manutenção.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirManutencao() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirManutencao(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_MANUTENCOES }),
  });
}

/** Variáveis de `useSalvarOficina`. */
interface SalvarOficinaVariaveis {
  /** Identificador da oficina a alterar, ou `null` para criar uma nova. */
  id: string | null;
  dados: NovaOficina;
}

/**
 * Cria ou altera uma oficina, conforme venha identificador.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarOficina() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarOficinaVariaveis) =>
      id === null ? criarOficina(dados) : atualizarOficina(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_OFICINAS }),
  });
}

/**
 * Apaga uma oficina da agenda.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirOficina() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirOficina(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_OFICINAS }),
  });
}
